import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useZoomPan } from '../../climbs/board-grid/useZoomPan';
import type { RouteHold } from '../../../types/competition-round';

/**
 * A circle's radius when it is first dropped, as a fraction of the photo's width.
 *
 * A route is shot from the ground, so its holds are small on the picture — but
 * every circle here is also a **target the judge taps** while an athlete is on the
 * wall, so it cannot be as small as the hold looks. This is the compromise: big
 * enough for a thumb, small enough that thirty of them on one photo do not merge.
 */
export const DEFAULT_ROUTE_HOLD_RADIUS = 0.035;

/** How far outside a circle a press still counts as being on it, on screen. */
const HIT_SLOP_PX = 8;

/** How far a press has to travel before it is a drag rather than a plain press. */
const DRAG_THRESHOLD_PX = 4;

/** How long a press has to be held to open the circle's menu. */
const LONG_PRESS_MS = 450;

/** The circle a press has hold of, and what it has done so far. */
interface Press {
  index: number;
  /** Where on screen the press landed, to tell a tap from a drag. */
  startX: number;
  startY: number;
  /** Where on the circle the press landed, in content fractions. */
  grabX: number;
  grabY: number;
  moved: boolean;
}

export interface RouteHoldsMenu {
  index: number;
  /** Where the circle sits, for the panel to hang off. */
  at: { x: number; y: number };
  radius: number;
}

export interface RouteHoldsInput {
  holds: RouteHold[];
  onChange: (holds: RouteHold[]) => void;
  /** Nothing may be drawn or moved — the drawing is only being looked at. */
  readOnly?: boolean;
}

export type RouteHoldsController = ReturnType<typeof useRouteHolds>;

/**
 * Circling the holds of a **lead route** on its photo.
 *
 * Deliberately not the climb editor's `usePhotoHolds`: that one is built around
 * what a hold *is* on a boulder — a start, a top, a foot, a match — and a
 * competition route has none of that. Here a circle is a hold and its number is
 * where it sits in the list, so there are exactly three gestures and no
 * vocabulary at all:
 *
 * - a press on the bare wall **drops the next circle** there;
 * - a press on a circle **selects** it, and dragging from there **moves** it;
 * - holding a circle down **opens its menu** (which, for now, deletes it).
 *
 * The numbering is never stored. Deleting circle 3 makes the old 4 the new 3, on
 * screen and in the column, because the number was only ever the index.
 *
 * Two fingers move and pinch the picture (see `useZoomPan`). One finger is the
 * pen, so a press that turns out to have hold of a circle takes the pan back
 * (`cancelPan`) — the one case where dragging means something other than moving
 * the photograph.
 */
export function useRouteHolds({ holds, onChange, readOnly = false }: RouteHoldsInput) {
  const zoom = useZoomPan();
  const [selected, setSelected] = useState<number | null>(null);
  const [menu, setMenu] = useState<RouteHoldsMenu | null>(null);

  // What the press has hold of, as a ref: the move handler reads it as the
  // pointer travels, not as it was when the press was set up.
  const pressRef = useRef<Press | null>(null);
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Where a press on the bare wall landed, waiting to become a circle when the
   * finger lifts. It waits because the same first finger begins a pinch, and a
   * gesture that turns out to be two fingers moving the picture must not leave a
   * circle behind where the first one touched down.
   */
  const pendingRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const pointersRef = useRef(new Set<number>());
  const multiTouchRef = useRef(false);

  const cancelLongPress = useCallback(() => {
    if (longPressRef.current) clearTimeout(longPressRef.current);
    longPressRef.current = null;
  }, []);

  useEffect(() => cancelLongPress, [cancelLongPress]);

  // A selection only means anything while the circle it points at is still
  // there: a shorter list — an undo, another route's drawing — drops it.
  useEffect(() => {
    setSelected((current) => (current !== null && current >= holds.length ? null : current));
  }, [holds.length]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenu(null);
        setSelected(null);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  /** The photo's box on screen, which presses are measured against. */
  const frameBox = () => zoom.frameRef.current?.getBoundingClientRect() ?? null;

  /**
   * Which circle a press at this point on the content means, or null for the bare
   * wall. **The last drawn wins** where two overlap: it is the one on top, and it
   * is the one somebody has just put there.
   */
  const hitTest = (point: { x: number; y: number }): number | null => {
    const box = frameBox();
    if (!box) return null;

    // A circle is round on screen, so its reach is its radius in the photo's
    // *width* either way — the press has to be measured in pixels for the two
    // axes to be comparable at all.
    const slop = HIT_SLOP_PX / zoom.scale;

    for (let index = holds.length - 1; index >= 0; index -= 1) {
      const hold = holds[index];
      const dx = (point.x - hold.x) * box.width;
      const dy = (point.y - hold.y) * box.height;
      const reach = hold.r * box.width + slop;

      if (dx * dx + dy * dy <= reach * reach) return index;
    }

    return null;
  };

  const moveHold = (index: number, x: number, y: number) =>
    onChange(
      holds.map((hold, at) =>
        at === index
          ? { ...hold, x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) }
          : hold
      )
    );

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    pointersRef.current.add(event.pointerId);
    if (pointersRef.current.size > 1) {
      // Two fingers are the picture's. Whatever the first one had armed is off.
      multiTouchRef.current = true;
      pendingRef.current = null;
      pressRef.current = null;
      cancelLongPress();
      return;
    }

    multiTouchRef.current = false;
    setMenu(null);

    const point = zoom.toContent(event.clientX, event.clientY);
    if (!point) return;

    const index = hitTest(point);

    if (index === null) {
      if (readOnly) return;
      // Nothing under the finger: a circle, once the finger lifts without
      // travelling and without a second one joining it.
      pendingRef.current = { x: point.x, y: point.y, startX: event.clientX, startY: event.clientY };
      return;
    }

    setSelected(index);
    if (readOnly) return;

    // The press has hold of a circle, so the picture is not what is moving.
    zoom.cancelPan();
    pressRef.current = {
      index,
      startX: event.clientX,
      startY: event.clientY,
      grabX: point.x - holds[index].x,
      grabY: point.y - holds[index].y,
      moved: false,
    };

    const hold = holds[index];
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null;
      // Held still long enough to be a question about the circle rather than a
      // move of it: the menu opens and the drag is abandoned.
      pressRef.current = null;
      setMenu({ index, at: { x: hold.x, y: hold.y }, radius: hold.r });
    }, LONG_PRESS_MS);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const pending = pendingRef.current;
    if (pending) {
      const travelled =
        Math.abs(event.clientX - pending.startX) > DRAG_THRESHOLD_PX ||
        Math.abs(event.clientY - pending.startY) > DRAG_THRESHOLD_PX;
      // A press that travels on the bare wall was the picture being dragged.
      if (travelled) pendingRef.current = null;
      return;
    }

    const press = pressRef.current;
    if (!press) return;

    if (!press.moved) {
      const travelled =
        Math.abs(event.clientX - press.startX) > DRAG_THRESHOLD_PX ||
        Math.abs(event.clientY - press.startY) > DRAG_THRESHOLD_PX;
      if (!travelled) return;

      // It is a drag. Whatever the hold-still timer was going to ask, it is not
      // being asked.
      press.moved = true;
      cancelLongPress();
    }

    const point = zoom.toContent(event.clientX, event.clientY);
    if (!point) return;

    moveHold(press.index, point.x - press.grabX, point.y - press.grabY);
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    pointersRef.current.delete(event.pointerId);
    cancelLongPress();

    const pending = pendingRef.current;
    pendingRef.current = null;
    pressRef.current = null;

    if (!pending || multiTouchRef.current || readOnly) return;

    // The next circle, at the end of the list — which is to say the next number.
    onChange([ ...holds, { x: pending.x, y: pending.y, r: DEFAULT_ROUTE_HOLD_RADIUS } ]);
    setSelected(holds.length);
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLDivElement>) {
    pointersRef.current.delete(event.pointerId);
    pendingRef.current = null;
    pressRef.current = null;
    cancelLongPress();
  }

  /**
   * Takes one circle out. Everything after it moves up a number, which is the
   * whole of what "the number is where it sits" costs and buys.
   */
  function deleteAt(index: number) {
    setMenu(null);
    setSelected(null);
    onChange(holds.filter((_, at) => at !== index));
  }

  return {
    zoom,
    holds,
    selected,
    select: setSelected,
    menu,
    closeMenu: () => setMenu(null),
    deleteAt,
    readOnly,
    frameProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerCancel,
    },
  };
}
